import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Landing from './pages/Landing';
import SignUp from './pages/SignUp';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Subscribe from './pages/Subscribe';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import { ToastProvider } from './context/ToastContext';
import API_URL from './config';
import './App.css';

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [sub, setSub] = useState(null); // null = loading, object = loaded

  useEffect(() => {
    if (!token) { setSub({ tier: 'none', status: 'none' }); return; }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    fetch(`${API_URL}/api/protected/subscription`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(r => r.ok ? r.json() : { tier: 'none', status: 'none' })
      .then(data => setSub(data))
      .catch(() => setSub({ tier: 'none', status: 'none' }))
      .finally(() => clearTimeout(timeout));
    return () => { controller.abort(); clearTimeout(timeout); };
  }, [token]);

  const handleLogin = (newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setSub(null); // will refetch via useEffect
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setSub({ tier: 'none', status: 'none' });
  };

  // Active means tier='standard' AND status is active, trialing, or past_due (not canceled)
  const subscribed = sub?.tier === 'standard' &&
    (sub?.status === 'active' || sub?.status === 'trialing' || sub?.status === 'past_due');
  const subLoaded = sub !== null;

  const dashboardElement = !token
    ? <Navigate to="/login" />
    : !subLoaded
      ? <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 32, height: 32, border: '3px solid var(--border)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      : subscribed
        ? <Dashboard token={token} onLogout={handleLogout} />
        : <Navigate to={window.location.search.includes('checkout=success') ? '/subscribe?checkout=success' : '/subscribe'} />;

  return (
    <ToastProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/signup" element={<SignUp onSignUp={handleLogin} />} />
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route
            path="/subscribe"
            element={token
              ? <Subscribe token={token} sub={sub} onSubUpdate={setSub} onLogout={handleLogout} />
              : <Navigate to="/login" />
            }
          />
          <Route path="/dashboard/*" element={dashboardElement} />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
